import React, { useEffect } from "react";
import "./OrderExpected.scss";
import { MotifIcon } from "@ey-xd/motif-react";
import {
  IconoirCheckCircle,
  IconoirEdit,
  IconoirArrowLeft,
} from "@ey-xd/motif-icon";
import { IconoirOpenNewWindow } from "@ey-xd/motif-icon";
import { SKU } from "../../../../store/slices/CheckoutSlice";
import Button from "../../../../components/Button/Button";
import { useNavigate } from "react-router";
import { ProductAccordian } from "./ProductAccordian";
import { setOrderId } from "../../../../utils/common";
import BreadcrumbHome from "../../../../components/BreadcrumbHome";
import { getTotalOrderAmountRow, getCostType } from "../../CheckoutHelper";
import InfoTooltip from "../../../../components/InfoTooltip";
import { variableTotalInfo } from "../../../../utils/skuTable";
import {
  fetchOpportunityName,
  resetOpportunityNameState,
} from "../../../../store/slices/OpportunityNameSlice";
import { RootState, AppDispatch } from "../../../../store";
import { useSelector, useDispatch } from "react-redux";
import Loader from "../../../../components/Loader";

export interface OrderResponseDetails {
  OrderId: number;
  OrderStatus: string;
  Description: string;
  EngagementId: string;
  OpportunityId: string;
  Details: {
    ProductName: string;
    AssetAdvice: string;
    SkuDetails: SKU[];
  }[];
  setIsSaveEstimate: React.Dispatch<React.SetStateAction<boolean>>;
  CRMSyncFailureStatus: number | null;
}

interface OrderExpectedProps {
  orderData: OrderResponseDetails | null;
  setIsSaveEstimate: React.Dispatch<React.SetStateAction<boolean>>;
  configData: any;
}

export const OrderExpected = ({
  orderData,
  setIsSaveEstimate,
  configData,
}: OrderExpectedProps) => {
  const nav = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const opportunityId = orderData?.OpportunityId?.trim() || "";

  useEffect(() => {
    dispatch(resetOpportunityNameState());

    if (opportunityId) {
      dispatch(
        fetchOpportunityName({
          Action: "getOpportunityName",
          opportunityCode: opportunityId,
        }),
      );
    }
    return () => {
      dispatch(resetOpportunityNameState());
    };
  }, [dispatch, opportunityId]);

  const opportunityNameInStore = useSelector(
    (state: RootState) => state.OpportunityNameSlice.data,
  );

  const opportunityNameResponse = Array.isArray(opportunityNameInStore)
    ? opportunityNameInStore[0]
    : "";
  const opportunityName =
    opportunityId &&
    opportunityNameResponse?.opportunityCode?.trim().toUpperCase() ===
      opportunityId.toUpperCase()
      ? (opportunityNameResponse?.opportunityName ?? "")
      : "";

  const opportunityNameLoadingFromStore = useSelector(
    (state: RootState) => state.OpportunityNameSlice.isLoading,
  );
  const opportunityNameLoading = Boolean(
    opportunityId && opportunityNameLoadingFromStore,
  );

  const getTotalOrderAmount = (checkoutListData: OrderResponseDetails) => {
    if (!checkoutListData || checkoutListData === null) {
      return 0;
    }
    if (checkoutListData && checkoutListData !== null) {
      let totalAmount = 0;
      if (checkoutListData?.Details.length === 0) {
        return 0;
      }
      checkoutListData.Details.forEach((orderDataRow: any) => {
        orderDataRow.SkuDetails.forEach((sku: any) => {
          const quantity = sku.OrderQuantity || 0;
          const cost = sku.CostMetricCost || 0;
          const duration = sku.OrderInterval || 0;
          totalAmount += cost * quantity * duration;
          if (sku.SkuRelation && sku.SkuRelation.length > 0) {
            sku.SkuRelation.forEach((relation: any) => {
              const quantity = relation.OrderQuantity || 0;
              const cost = relation.CostMetricCost || 0;
              const duration = relation.OrderInterval || 0;
              totalAmount += cost * quantity * duration;
            });
          }
        });
      });
      return totalAmount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    }
  };

  const getTotalOrderAmountVariable = (
    checkoutListData: OrderResponseDetails,
  ) => {
    let totalAmount = 0;
    checkoutListData.Details.forEach((order: any) => {
      order.SkuDetails.forEach((sku: any) => {
        const costType = getCostType(sku.CostMetricCd, configData);
        if (!(costType === "Fixed")) {
          totalAmount += getTotalOrderAmountRow(sku);
        }
      });
    });
    return totalAmount;
  };

  const variableCost = orderData ? getTotalOrderAmountVariable(orderData) : 0;

  const breadcrumbItems = [
    {
      label: "Home",
      value: "Home",
      elementKey: "submission",
      text: "Home",
      url: `/`,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
    {
      label: "Estimate Saved",
      value: "Estimate Saved",
      elementKey: "submission",
      text: "Estimate Saved",
      url: `/marketplace`,
      disabled: true,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
  ];

  if (!orderData || !orderData.Details || orderData.Details.length === 0) {
    return (
      <div className="motif-container order-expected-placed-error">
        <div className="motif-row">
          <div className="motif-col-12">
            <div className="order-expected-placed-error-text">
              No order data available.
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="order-expected-header">
        <div className="product-cost-setup-bredcrumb">
          <BreadcrumbHome
            items={breadcrumbItems}
            ariaLabel="Breadcrumb navigation"
          />
        </div>
        <div className="success-grid">
          <div className="success-grid-icon">
            <MotifIcon
              iconFunction={IconoirCheckCircle}
              size="28"
              fill="#26A04B"
              strokeWidth="5px"
            />
          </div>
          <div className="success-grid-text">
            <div className="success-grid-text-title">
              Estimate saved successfully
            </div>
            <div className="success-grid-order-description">
              Your estimate has been saved. You can review the details below.
            </div>
            {orderData.CRMSyncFailureStatus != null &&
              orderData.CRMSyncFailureStatus >= 1 && (
                <div className="success-grid-order-description order-expected-description">
                  We’re currently unable to sync it with the CRM, but you can
                  retry the sync later from{" "}
                  <span
                    className="order-expected-link-value"
                    onClick={() => nav("/my-workspace/my-estimates")}
                  >
                    My Estimates
                  </span>
                  .
                </div>
              )}
          </div>
        </div>
      </div>
      <div className="motif-container order-expected-success-content">
        <div className="motif-row order-expected-number-grid">
          <div className="motif-col-xs-12 motif-col-sm-12 motif-col-md-3 motif-col-lg-3 align-left-middle">
            <div className="order-expected-number-heading">
              {orderData.Description}
            </div>
            <div className="order-expected-number-value">
              {orderData.OrderId}
            </div>
          </div>
          <div className="motif-col-xs-12 motif-col-sm-12 motif-col-md-3 motif-col-lg-3 align-left-middle">
            <div className="order-expected-number-heading">
              {opportunityNameLoading ? (
                <Loader />
              ) : opportunityName ? (
                opportunityName
              ) : (
                "Opportunity ID"
              )}
            </div>
            <div className="order-expected-number-value">
              {orderData?.OpportunityId || "--"}
            </div>
          </div>
          <div className="motif-col-xs-12 motif-col-sm-12 motif-col-md-3 motif-col-lg-3 align-left-middle">
            <div className="order-expected-number-heading">
              View saved estimates
            </div>
            <div
              className="order-expected-link-value"
              onClick={() => nav("/my-workspace/my-estimates")}
            >
              My estimates
              <MotifIcon
                iconFunction={IconoirOpenNewWindow}
                size="13"
                fill="#35A4E8"
                className="link-icon"
              />
            </div>
          </div>
          <div className="motif-col-xs-12 motif-col-sm-12 motif-col-md-3 motif-col-lg-3 align-right-middle">
            <div className="order-expected-number-heading right-align">
              Total Cost
            </div>
            <div className="order-expected-number-value right-align total-cost">
              {getTotalOrderAmount(orderData)}
            </div>
            {variableCost > 0 && (
              <div className="right-align">
                <div className="variable-cost-info-grid">
                  <div className="variable-cost">
                    {variableCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}{" "}
                    Variable cost
                  </div>
                  <InfoTooltip
                    description={variableTotalInfo()}
                    info={true}
                    id="variable-total-cost-info"
                    size="14"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="motif-row order-expected-summary">
          <div className="motif-col-lg-12 order-expected-summary-header">
            <div className="order-expected-summary-header-title">
              Order Summary
            </div>
            <div className="order-expected-summary-header-description">
              Products and services you're currently using across your
              engagements
            </div>
          </div>
          <table className="motif-table-standard">
            <thead>
              <tr>
                <th className="sku-th">SKU#/ SKU Name</th>
                <th>Quantity</th>
                <th>Unit Cost</th>
                <th>Duration</th>
                <th>Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {orderData?.Details.map((orderDataRow, index) => (
                <ProductAccordian
                  key={index}
                  skuDetails={orderDataRow.SkuDetails}
                  productName={orderDataRow.ProductName}
                />
              ))}
            </tbody>
          </table>
        </div>
        <div className="motif-row">
          <div className="motif-col-lg-12">
            <div className="order-expected-placed-go-to-home">
              <Button
                className="order-expected-placed-button"
                variant="secondary"
                label="Edit Estimate"
                onClickHandler={() => {
                  setIsSaveEstimate(false);
                  setOrderId("" + orderData?.OrderId);
                }}
                children={
                  <MotifIcon
                    iconFunction={IconoirEdit}
                    size="20"
                    fill="#fff"
                    strokeWidth="5px"
                  />
                }
              />
              <Button
                className="order-expected-placed-button got-to-home-btn"
                variant="primary"
                label="Back To Home Page"
                onClickHandler={() => {
                  nav("/");
                }}
              />
            </div>
            <div className="order-expected-placed-go-to-home"></div>
          </div>
        </div>
      </div>
    </>
  );
};
